import React, {useCallback, useEffect, useState} from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonContent,
    IonLabel, IonSelect, IonSelectOption, IonInput,
    IonFooter, IonRow, IonCol, IonButton, IonItem,
    useIonAlert, IonTextarea, IonSearchbar, IonList, IonIcon, IonButtons
} from "@ionic/react";
import {
    _getIndianStates,
    _getManglikStatusOptions,
    _getMaritalStatusOptions,
    _getReligionOptions
} from "../apiHelpers/CommonHelper";
import {
    actionToEditUpdateUserMatrimonialProfileData,
    actionToGetMatrimonialPostingByFilter,
    actionToGetUserMatrimonialProfileData,
    actionToGetUserSessionData,
    actionToUploadUserMatrimonialProfileData
} from "../apiHelpers/CommonAction";
import moment from "moment-timezone";
import {arrowBack, star} from "ionicons/icons";
import useStore from "../zustand/useStore";

export default function AddMatrimonialCandidateProfileModal({ isOpen,dataForEditing,matrimonialPortalRole, onClose, setIsLoading }) {
    const [presentAlert] = useIonAlert();
    const [errors, setErrors] = useState({});
    const [showPopover, setShowPopover] = useState(false);
    const [searchText, setSearchText] = useState("");

    const filteredStates = _getIndianStates.filter(state =>
        state.toLowerCase().includes(searchText.toLowerCase())
    );
    const {userAuthDetail:{userInfo}} = useStore();

    const [form, setForm] = useState({
        posted_by_name: userInfo?.name,
        name: userInfo?.name,
        email: userInfo?.email,
        phone: userInfo?.phone_number,
        date_of_birth: '',
        time_of_birth: '',
        place_of_birth: '',
        gender: '',
        height: '',
        religion: '',
        caste: '',
        occupation: '',
        education: '',
        location: '',
        profile_for: '',
        profilePic: [],
        profilePdf: null,
        created_by: '',

        contact_person: '',
        about_me: '',

        father_occupation: '',
        mother_occupation: '',
        siblings: '',

        partner_age_min: '',
        partner_age_max: '',
        partner_religion: '',
        partner_location: '',
        partner_education: '',

        marital_status: '',
        manglik_status: ''
    });

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const handleImageChange = (files) => {
        if (files.length > 3) {
            setErrors(prev => ({
                ...prev,
                profilePic: "You can upload a maximum of 3 images."
            }));
            return;
        }
        setForm(prev => ({ ...prev, profilePic: files }));
        setErrors(prev => ({ ...prev, profilePic: "" }));
    };

    const handleProfilePdfChange = (file) => {
        if (file?.type.startsWith("application/pdf")) {
            setForm(prev => ({ ...prev, profilePdf: file }));
            setErrors(prev => ({ ...prev, profilePdf: '' }));
        } else {
            setForm(prev => ({ ...prev, profilePdf: null }));
            setErrors(prev => ({ ...prev, profilePdf: 'Only PDF files allowed.' }));
        }
    };

    const validateForm = () => {
        let newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{10}$/;

        if (!form.posted_by_name.trim()) newErrors.posted_by_name = "Posted By Name is required";
        if (!form.name.trim()) newErrors.name = "Name is required";

        if (!form.date_of_birth) newErrors.age = "Date of birth is required";
        if (!form.time_of_birth) newErrors.age = "Time of birth is required";
        if (!form.place_of_birth) newErrors.age = "Place of birth is required";

        if (!form.height || isNaN(form.height) || Number(form.height) <= 0) newErrors.height = "Valid height is required";

        if (!form.profile_for) newErrors.profile_for = "Profile for is required";
        if (!form.religion) newErrors.religion = "Religion is required";
        if (!form.caste.trim()) newErrors.caste = "Caste is required";
        if (!form.occupation.trim()) newErrors.occupation = "Occupation is required";
        if (!form.education || !form?.education?.length) newErrors.education = "Education is required";
        if (!form.location) newErrors.location = "Location is required";

        if (!phoneRegex.test(form.phone)) newErrors.phone = "Valid 10-digit phone number is required";
        if (!form.email.trim() || !emailRegex.test(form.email)) newErrors.email = "Valid email is required";

        if (!form.about_me.trim()) newErrors.about_me = "About Me is required";

        if (!form.marital_status) newErrors.marital_status = "Marital status is required";
        if (!form.manglik_status) newErrors.manglik_status = "Manglik status is required";

        if (!form.father_occupation.trim()) newErrors.father_occupation = "Father's occupation is required";
        if (!form.mother_occupation.trim()) newErrors.mother_occupation = "Mother's occupation is required";
        if (!form.siblings.trim()) newErrors.siblings = "Siblings information is required";

        if (!form.partner_age_min) newErrors.partner_age = "Partner age is required";
        if (!form.partner_age_max) newErrors.partner_age = "Partner age is required";
        if (!form.partner_religion) newErrors.partner_religion = "Preferred partner religion is required";
        if (!form.partner_location || !form?.partner_location?.length) newErrors.partner_location = "Preferred partner location is required";
        if (!form.partner_education || !form?.partner_education?.length) newErrors.partner_education = "Preferred partner education is required";

        if(!dataForEditing?.id) {
            if (!form.profilePic || form.profilePic.length === 0) newErrors.profilePic = "Profile image is required";
            if (!form.profilePdf) newErrors.profilePdf = "User Bio data PDF is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;
        const formData = new FormData();
        // Append simple fields except files
        Object.entries(form).forEach(([key, val]) => {
            if (key !== 'profilePic' && key !== 'profilePdf' && key !== 'date_of_birth') {
                if (Array.isArray(val)) {
                    formData.append(key, val.join(','));
                } else {
                    formData.append(key, val ?? '');
                }
            }
        });

        // Append images
        if (form.profilePic?.length) {
            form.profilePic.forEach(file => {
                formData.append('profilePic', file);
            });
        }

        // Append PDF
        if (form.profilePdf) {
            formData.append("profilePdf", form.profilePdf);
        }

        // Age calculation
        const dob = new Date(form.date_of_birth);
        const today = new Date();
        let ageInYears = today.getFullYear() - dob.getFullYear();
        if (
            today.getMonth() < dob.getMonth() ||
            (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
        ) {
            ageInYears--;
        }

        // Append calculated + combined fields
        formData.append("date_of_birth", moment(form.date_of_birth).format('YYYY-MM-DD'));
        formData.append("age", ageInYears);
        formData.append("partner_age", `${form.partner_age_min}-${form.partner_age_max}`);

        // Handle edit vs new
        if (dataForEditing?.id) {
            formData.append('id', dataForEditing?.id);
            formData.append('existing_image_url', dataForEditing?.image_url);
            formData.append('existing_pdf_url', dataForEditing?.pdf_url);
            formData.append('user_role', dataForEditing?.gender_code);
        } else {
            formData.append('user_role', matrimonialPortalRole);
        }

        setIsLoading(true);

        try {
            if (dataForEditing?.id) {
                await actionToEditUpdateUserMatrimonialProfileData(formData);
                actionToGetMatrimonialPostingByFilter(true, {me_mode:true});
                actionToGetUserMatrimonialProfileData();
                onClose();
            } else {
                await actionToUploadUserMatrimonialProfileData(formData);
                actionToGetUserSessionData();
                onClose();
            }
        } catch (err) {
            presentAlert({
                message: 'Something went wrong while uploading.',
                buttons: ['OK'],
            });
        } finally {
            setIsLoading(false);
        }
    },[form]);

    useEffect(() => {
        if (isOpen) {
            if (dataForEditing?.id) {
                // Editing → populate form
                setForm({
                    posted_by_name: dataForEditing.posted_by_name || userInfo?.name,
                    name: dataForEditing.name || userInfo?.name,
                    date_of_birth: moment(dataForEditing.date_of_birth).format('YYYY-MM-DD') || '',
                    time_of_birth: dataForEditing.time_of_birth || '',
                    place_of_birth: dataForEditing.place_of_birth || '',
                    gender: dataForEditing.gender || '',
                    height: dataForEditing.height || '',
                    religion: dataForEditing.religion || '',
                    caste: dataForEditing.caste || '',
                    occupation: dataForEditing.occupation || '',
                    education: dataForEditing?.education?.split(',') || [],
                    location: dataForEditing.location || '',
                    profile_for: dataForEditing.profile_for || '',
                    created_by: dataForEditing.created_by || '',

                    contact_person: dataForEditing.contact_person || '',
                    phone: dataForEditing.phone || userInfo?.phone_number,
                    email: dataForEditing.email || userInfo?.email,
                    about_me: dataForEditing.about_me || '',

                    father_occupation: dataForEditing.father_occupation || '',
                    mother_occupation: dataForEditing.mother_occupation || '',
                    siblings: dataForEditing.siblings || '',

                    partner_age_min: dataForEditing.partner_age_min || '',
                    partner_age_max: dataForEditing.partner_age_max || '',
                    partner_religion: dataForEditing.partner_religion || '',
                    partner_location: dataForEditing?.partner_location?.split(',') || [],
                    partner_education: dataForEditing?.partner_education?.split(',') || [],

                    marital_status: dataForEditing.marital_status || '',
                    manglik_status: dataForEditing.manglik_status || ''
                });
            } else {
                // Adding → reset form
                setForm({
                    posted_by_name: userInfo?.name,
                    name: userInfo?.name,
                    email: userInfo?.email,
                    phone: userInfo?.phone_number,
                    date_of_birth: '',
                    time_of_birth: '',
                    place_of_birth: '',
                    gender: '',
                    height: '',
                    religion: '',
                    caste: '',
                    occupation: '',
                    education: '',
                    location: '',
                    profile_for: '',
                    profilePic: [],
                    profilePdf: null,
                    created_by: '',

                    contact_person: '',
                    about_me: '',

                    father_occupation: '',
                    mother_occupation: '',
                    siblings: '',

                    partner_age_min: '',
                    partner_age_max: '',
                    partner_religion: '',
                    partner_location: '',
                    partner_education: '',

                    marital_status: '',
                    manglik_status: ''
                });
            }
        }
    }, [dataForEditing, isOpen]);


    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            initialBreakpoint={1}
            breakpoints={[0, 1]}
            handleBehavior="cycle"
        >
            <IonHeader className="filter_sheet_header sheet_modal_header">
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton className={"close_job_post_button"} onClick={onClose}>
                            <IonIcon icon={arrowBack}/>
                        </IonButton>
                    </IonButtons>
                    <IonTitle className="filter_sheet_header_text">Add Matrimonial Profile</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding bottom-2 no_padding_top">
                <div className="add-job-content">
                    <div className="rfp_submit_form_item">
                        <IonLabel>Name<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            value={form.name}
                            placeholder="Enter full name"
                            onIonInput={(e) => handleChange('name', e.detail.value)}
                        />
                        {errors.name && <p className="error-text">{errors.name}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Posted By Name<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            value={form.posted_by_name}
                            placeholder="Enter full name"
                            onIonInput={(e) => handleChange('posted_by_name', e.detail.value)}
                        />
                        {errors.posted_by_name && <p className="error-text">{errors.posted_by_name}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Profile For<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.profile_for}
                            placeholder="Select relationship"
                            onIonChange={(e) => handleChange('profile_for', e.detail.value)}
                        >
                            <IonSelectOption value="Self">Self</IonSelectOption>
                            <IonSelectOption value="Sibling">Sibling</IonSelectOption>
                            <IonSelectOption value="Parent">Parent</IonSelectOption>
                            <IonSelectOption value="Relative">Relative</IonSelectOption>
                            <IonSelectOption value="Friend">Friend</IonSelectOption>
                        </IonSelect>
                        {errors.profile_for && (
                            <p className="error-text">{errors.profile_for}</p>
                        )}
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonRow className={"rfp_submit_form_item_row_sub"}>
                            <IonCol size={6}>
                                <IonLabel>Date of birth<IonIcon icon={star} className="required-star" /></IonLabel>
                                <IonInput
                                    type="date"
                                    className="rfp_form_input"
                                    value={form.date_of_birth}
                                    placeholder="Enter date of birth"
                                    min={new Date(new Date().setFullYear(new Date().getFullYear() - 60)).toISOString().split("T")[0]}
                                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
                                    onIonInput={(e) => handleChange('date_of_birth', e.detail.value)}
                                />
                            </IonCol>
                            <IonCol size={6}>
                                <IonLabel>Time of birth<IonIcon icon={star} className="required-star" /></IonLabel>
                                <IonInput
                                    type="time"
                                    className="rfp_form_input"
                                    value={form.time_of_birth}
                                    placeholder="Enter time of birth"
                                    onIonInput={(e) => handleChange('time_of_birth', e.detail.value)}
                                />
                            </IonCol>
                        </IonRow>
                        {errors.age && <p className="error-text">{errors.age}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonRow className={"rfp_submit_form_item_row_sub"}>
                            <IonCol size={12}>
                                <IonLabel>Place of birth<IonIcon icon={star} className="required-star" /></IonLabel>
                                <IonInput
                                    type="text"
                                    className="rfp_form_input"
                                    value={form.place_of_birth}
                                    placeholder="Enter place of birth"
                                    onIonInput={(e) => handleChange('place_of_birth', e.detail.value)}
                                />
                            </IonCol>
                        </IonRow>
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonLabel>Height (in Inches)<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            type="number"
                            value={form.height}
                            placeholder={`e.g., 5.1`}
                            onIonInput={(e) => handleChange('height', e.detail.value)}
                        />
                        {errors.height && <p className="error-text">{errors.height}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Religion<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.religion}
                            placeholder="Select religion"
                            onIonChange={(e) => handleChange('religion', e.detail.value)}
                        >
                            {_getReligionOptions.map((religion) => (
                                <IonSelectOption key={religion} value={religion}>
                                    {religion}
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                        {errors.religion && <p className="error-text">{errors.religion}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Caste<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            value={form.caste}
                            placeholder="Enter caste"
                            onIonInput={(e) => handleChange('caste', e.detail.value)}
                        />
                        {errors.caste && <p className="error-text">{errors.caste}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Occupation<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            value={form.occupation}
                            placeholder="e.g., Doctor, Engineer"
                            onIonInput={(e) => handleChange('occupation', e.detail.value)}
                        />
                        {errors.occupation && <p className="error-text">{errors.occupation}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Education<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.education}
                            multiple={true}
                            placeholder="Select education"
                            onIonChange={(e) => handleChange('education', e.detail.value)}
                        >
                            <IonSelectOption value="Chartered Accountant">Chartered Accountant</IonSelectOption>
                            <IonSelectOption value="Company Secretary">Company Secretary</IonSelectOption>
                            <IonSelectOption value="Advocate">Advocate</IonSelectOption>
                            <IonSelectOption value="Engineer">Engineer</IonSelectOption>
                            <IonSelectOption value="Business / Entrepreneur">Business / Entrepreneur</IonSelectOption>
                            <IonSelectOption value="Graduate">Graduate</IonSelectOption>
                            <IonSelectOption value="Others">Others</IonSelectOption>
                        </IonSelect>
                        {errors.education && <p className="error-text">{errors.education}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Phone Number<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            type="tel"
                            placeholder={"Enter your phone number"}
                            className="rfp_form_input"
                            value={form.phone}
                            onIonInput={(e) => handleChange('phone', e.detail.value)}
                        />
                        {errors.phone && <p className="error-text">{errors.phone}</p>}
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonLabel>Email<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            type="email"
                            placeholder={"Enter your email address"}
                            className="rfp_form_input"
                            value={form.email}
                            onIonInput={(e) => handleChange('email', e.detail.value)}
                        />
                        {errors.email && <p className="error-text">{errors.email}</p>}
                    </div>

                    <div className={"rfp_submit_form_item"}>
                        <IonLabel>Location<IonIcon icon={star} className="required-star" /></IonLabel>
                        <div onClick={() => setShowPopover(true)} className={"select_popup_injecttor_sele"}>
                            {form?.location ? <span className={"actual_selection"}>{form?.location}</span> :
                                <span className={"select_place_holder"}>Select Location <svg
                                    xmlns="http://www.w3.org/2000/svg" fill="#5e5e5e" width="10px" height="10px" className="ionicon" viewBox="0 0 512 512"><path
                                    d="M64 144l192 224 192-224H64z"></path></svg></span>}
                        </div>

                        <IonModal
                            isOpen={showPopover}
                            onDidDismiss={() => setShowPopover(false)}
                            cssClass="custom-alert-modal"
                        >
                            <div className="custom-alert-box">
                                <h2 className="alert-title">Select Location</h2>

                                <IonSearchbar
                                    value={searchText}
                                    onIonInput={(e) => setSearchText(e.detail.value)}
                                    placeholder="Search..."
                                />

                                <IonList className="alert-list">
                                    {filteredStates.map((state, idx) => (
                                        <IonItem
                                            key={idx}
                                            button
                                            onClick={() => {
                                                handleChange("location", state);
                                                setShowPopover(false);
                                            }}
                                        >
                                            {state}
                                        </IonItem>
                                    ))}
                                </IonList>
                            </div>
                        </IonModal>
                        {errors.location && <p className="error-text">{errors.location}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>About Me<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonTextarea
                            className="rfp_form_input"
                            placeholder={"Enter some thing about you"}
                            rows={2}
                            value={form.about_me}
                            onIonInput={(e) => handleChange('about_me', e.detail.value)}
                        />
                        {errors.about_me && <p className="error-text">{errors.about_me}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Marital Status<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.marital_status}
                            placeholder="Select marital status"
                            onIonChange={(e) => handleChange('marital_status', e.detail.value)}
                        >
                            {_getMaritalStatusOptions.map((status) => (
                                <IonSelectOption key={status} value={status}>
                                    {status}
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                        {errors.marital_status && <p className="error-text">{errors.marital_status}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel position="stacked">Manglik Status<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.manglik_status}
                            placeholder="Select manglik status"
                            onIonChange={(e) => handleChange('manglik_status', e.detail.value)}
                        >
                            {_getManglikStatusOptions.map((status) => (
                                <IonSelectOption key={status} value={status}>
                                    {status}
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                        {errors.manglik_status && <p className="error-text">{errors.manglik_status}</p>}
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonLabel position="floating">{`Father's`} Occupation<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            placeholder="Enter father's occupation"
                            value={form.father_occupation}
                            onIonInput={(e) => handleChange('father_occupation', e.detail.value)}
                        />
                        {errors.father_occupation && <p className="error-text">{errors.father_occupation}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel position="floating">{`Mother's`} Occupation<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            placeholder="Enter mother's occupation"
                            value={form.mother_occupation}
                            onIonInput={(e) => handleChange('mother_occupation', e.detail.value)}
                        />
                        {errors.mother_occupation && <p className="error-text">{errors.mother_occupation}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel position="floating">Siblings<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonInput
                            className="rfp_form_input"
                            placeholder="Enter siblings details"
                            value={form.siblings}
                            onIonInput={(e) => handleChange('siblings', e.detail.value)}
                        />
                        {errors.siblings && <p className="error-text">{errors.siblings}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Preferred Partner Age<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonRow className={"rfp_submit_form_item_row_sub"}>
                            <IonCol>
                                <IonLabel>Min Age</IonLabel>
                                <IonInput placeholder="Enter Min Age" type={"number"} className={"rfp_form_input"} value={form.partner_age_min} onIonInput={e => handleChange('partner_age_min', e.detail.value)} />
                            </IonCol>
                            <IonCol>
                                <IonLabel>Max Age</IonLabel>
                                <IonInput placeholder="Enter Max Age" type={"number"} className={"rfp_form_input"} value={form.partner_age_max} onIonInput={e => handleChange('partner_age_max', e.detail.value)} />
                            </IonCol>
                        </IonRow>
                        {errors.partner_age && <p className="error-text">{errors.partner_age}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Preferred Religion<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.partner_religion}
                            placeholder="Select religion"
                            onIonChange={(e) => handleChange('partner_religion', e.detail.value)}
                        >
                            {_getReligionOptions.map((r, idx) => (
                                <IonSelectOption key={idx} value={r}>{r}</IonSelectOption>
                            ))}
                        </IonSelect>
                        {errors.partner_religion && <p className="error-text">{errors.partner_religion}</p>}
                    </div>

                    <div className="rfp_submit_form_item">
                        <IonLabel>Preferred Location<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            placeholder="Select preferred locations"
                            value={form.partner_location}
                            multiple={true}
                            onIonChange={(e) => handleChange('partner_location', e.detail.value)}
                        >
                            <IonSelectOption value="My City">My City</IonSelectOption>
                            <IonSelectOption value="Any Metro">Any Metro</IonSelectOption>
                            <IonSelectOption value="Anywhere in India">Anywhere in India</IonSelectOption>
                            <IonSelectOption value="Anywhere">Anywhere</IonSelectOption>

                            {_getIndianStates.map((state, index) => (
                                <IonSelectOption key={index} value={state}>
                                    {state}
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                        {errors.partner_location && (
                            <p className="error-text">{errors.partner_location}</p>
                        )}
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonLabel>Preferred Education<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonSelect
                            className="rfp_form_input"
                            value={form.partner_education}
                            multiple={true} // Allow multiple selections
                            placeholder="Select preferred education"
                            onIonChange={(e) => handleChange('partner_education', e.detail.value)}
                        >
                            <IonSelectOption value="Chartered Accountant">Chartered Accountant</IonSelectOption>
                            <IonSelectOption value="Company Secretary">Company Secretary</IonSelectOption>
                            <IonSelectOption value="Advocate">Advocate</IonSelectOption>
                            <IonSelectOption value="Engineer">Engineer</IonSelectOption>
                            <IonSelectOption value="Business / Entrepreneur">Business / Entrepreneur</IonSelectOption>
                            <IonSelectOption value="Graduate">Graduate</IonSelectOption>
                            <IonSelectOption value="Post Graduate">Post Graduate</IonSelectOption>
                            <IonSelectOption value="Others">Others</IonSelectOption>
                        </IonSelect>
                        {errors.partner_education && (
                            <p className="error-text">{errors.partner_education}</p>
                        )}
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonLabel>Upload Profile Images (Max 3)<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonItem className="ion-no-padding ion-margin-bottom">
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="attach-pdf"
                                onChange={(e) =>
                                    handleImageChange(Array.from(e.target.files))
                                }
                            />
                        </IonItem>
                        {errors.profilePic && (
                            <p className="error-text">{errors.profilePic}</p>
                        )}
                    </div>


                    <div className="rfp_submit_form_item">
                        <IonLabel>Upload Detailed Bio Data (pdf)<IonIcon icon={star} className="required-star" /></IonLabel>
                        <IonItem className="ion-no-padding ion-margin-bottom">
                            <input
                                type="file"
                                accept="application/pdf"
                                className="attach-pdf"
                                onChange={(e) => handleProfilePdfChange(e.target.files[0])}
                            />
                        </IonItem>
                        {errors.profilePdf && <p className="error-text">{errors.profilePdf}</p>}
                    </div>

                </div>
            </IonContent>

            <IonFooter className="job_detail_sheet_footer ion-padding">
                <IonToolbar>
                <IonRow>
                    <IonCol>
                        <IonButton className="close_job_detail_button" expand="block"
                                   onClick={onClose}>Close</IonButton>
                    </IonCol>
                    <IonCol>
                        <IonButton className="apply_job_button" onClick={()=>handleSubmit()} expand="block">
                            Submit Profile
                        </IonButton>
                    </IonCol>
                </IonRow>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    );
}
